import React from "react";
import { useSelector } from "react-redux";

const TYPE_OPTS     = ["ALL", "TEXT", "NUMBER", "DATE", "BOOLEAN"];
const REQ_OPTS      = ["YES", "NO"];

function IconPlus() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}
function IconUp() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 15l-6-6-6 6" />
    </svg>
  );
}
function IconDown() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

function SIField({ label, value, onChange, disabled }) {
  return (
    <div className="xsie-field">
      <label className="xsie-field-label">{label}</label>
      <input
        type="text"
        value={value || ""}
        onChange={onChange}
        disabled={disabled}
        className={"xsie-field-input" + (disabled ? " xsie-field-input--disabled" : "")}
      />
    </div>
  );
}

function SISelect({ label, value, opts, onChange }) {
  return (
    <div className="xsie-field">
      <label className="xsie-field-label">{label}</label>
      <select value={value || opts[0]} onChange={onChange} className="xsie-field-select">
        {opts.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

const ScriptInputsEditor = ({ value, onChange }) => {
  const [expandedIdx, setExpandedIdx] = React.useState(null);
  const { defConfig, defLang } = useSelector((state) => state.Login);
  const c = defConfig?.[defLang] || {};

  const inputs = Array.isArray(value) ? value : [];

  const notify = (next) => onChange(next);

  const add = () => {
    const next = [...inputs, { INPUTNAME: "", INPUTLABEL: "", TYPE: "ALL", IS_REQUIRED: "NO", _isNew: true }];
    notify(next);
    setExpandedIdx(next.length - 1);
  };

  const remove = (idx) => {
    notify(inputs.filter((_, i) => i !== idx));
    setExpandedIdx(null);
  };

  const move = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= inputs.length) return;
    const next = [...inputs];
    [next[idx], next[target]] = [next[target], next[idx]];
    notify(next);
    setExpandedIdx(prev => prev === idx ? target : prev === target ? idx : prev);
  };

  const update = (idx, key, val) => {
    notify(inputs.map((item, i) => i === idx ? { ...item, [key]: val } : item));
  };

  return (
    <div className="xsie-wrapper">
      {/* Header */}
      <div className="xsie-header">
        <div className="xsie-header-left">
          <span className="xsie-title">{c.mcnInputs || "Inputs"}</span>
          {inputs.length > 0 && (
            <span className="xsie-count">{inputs.length}</span>
          )}
        </div>
        <button type="button" className="xsie-add-btn" onClick={add}>
          <IconPlus />
          {c.mcnAdd || "Add"}
        </button>
      </div>

      {/* List */}
      <div className="xsie-list">
        {inputs.length === 0 ? (
          <div className="xsie-empty">
            {c.mcnNoInputsDefined || "No inputs defined — use \"Add\" to add parameters to the connector"}
          </div>
        ) : inputs.map((item, idx) => {
          const isOpen = expandedIdx === idx;
          return (
            <div key={idx} className={"xsie-item" + (isOpen ? " xsie-item--open" : "")}>
              {/* Row */}
              <div
                className="xsie-row"
                onClick={() => setExpandedIdx(isOpen ? null : idx)}
              >
                <div className="xsie-row-info">
                  <span className="xsie-row-name">{item.INPUTNAME || (c.mcnNoName || "(no name)")}</span>
                  <span className="xsie-row-sub">
                    {item.INPUTLABEL || (c.mcnNoLabel || "no label")}
                    {item.IS_REQUIRED === "YES" && <span className="xsie-badge xsie-badge--req">REQ</span>}
                    <span className="xsie-badge xsie-badge--type">{item.TYPE || "ALL"}</span>
                  </span>
                </div>
                <div className="xsie-row-actions" onClick={e => e.stopPropagation()}>
                  <button
                    type="button"
                    className="xsie-order-btn"
                    disabled={idx === 0}
                    onClick={() => move(idx, -1)}
                    title={c.mcnMoveUp || "Move up"}
                  ><IconUp /></button>
                  <button
                    type="button"
                    className="xsie-order-btn"
                    disabled={idx === inputs.length - 1}
                    onClick={() => move(idx, 1)}
                    title={c.mcnMoveDown || "Move down"}
                  ><IconDown /></button>
                </div>
                <span className="xsie-chevron">{isOpen ? "▲" : "▼"}</span>
              </div>

              {/* Expanded form */}
              {isOpen && (
                <div className="xsie-expand">
                  <div className="xsie-expand-grid">
                    <SIField
                      label={c.mcnInputName || "Input Name"}
                      value={item.INPUTNAME}
                      disabled={!item._isNew}
                      onChange={e => update(idx, "INPUTNAME", e.target.value)}
                    />
                    <SIField
                      label={c.mcnInputLabel || "Input Label"}
                      value={item.INPUTLABEL}
                      onChange={e => update(idx, "INPUTLABEL", e.target.value)}
                    />
                  </div>
                  <div className="xsie-expand-grid xsie-expand-grid--2">
                    <SISelect
                      label={c.mcnType || "Type"}
                      value={item.TYPE}
                      opts={TYPE_OPTS}
                      onChange={e => update(idx, "TYPE", e.target.value)}
                    />
                    <SISelect
                      label={c.mcnRequired || "Required"}
                      value={item.IS_REQUIRED}
                      opts={REQ_OPTS}
                      onChange={e => update(idx, "IS_REQUIRED", e.target.value)}
                    />
                  </div>
                  <button
                    type="button"
                    className="xsie-delete-btn"
                    onClick={() => remove(idx)}
                  >
                    {c.mcnDeleteInput || "Delete input"}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ScriptInputsEditor;
